/**
 * EmailAutosave — manages draft autosave for the compose form.
 *
 * Usage:
 *   const autosave = new EmailAutosave({ store, getPayload, onSaved, onCreated })
 *   autosave.start()
 *   autosave.stop()
 *   autosave.saveNow()     // manual trigger
 *   autosave.notifyChange() // call on every keystroke for debounced save
 *   autosave.deleteDraft() // deletes the saved draft from server
 *
 * getPayload must return an object like:
 *   { email_draft: { mailbox_id, subject, body, to_addresses, cc_addresses, draft_type, reply_to_email_id } }
 * or null/undefined to skip the save.
 */

import { apiPost, apiPatch, apiDelete, emailApiBase } from './store/api.js'
import { buildDraftPath } from '@tshmanager/email-shared'

const DEFAULT_INTERVAL_MS = 8000   // periodic poll interval
const DEBOUNCE_MS         = 2000   // save this many ms after last keystroke

/**
 * Strips signature/quote blocks from an HTML body and returns the remaining
 * plain-text content (whitespace-collapsed). Used to detect "no real user
 * content yet" so autosave never creates an empty draft that only contains
 * the mailbox signature and/or the quoted original message.
 */
function meaningfulText(html) {
  if (!html) return ''
  if (typeof document === 'undefined') return String(html).trim()
  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('.email-signature, .email-quoted-content, blockquote').forEach(el => el.remove())
  return (div.textContent || '').replace(/\s+/g, ' ').trim()
}

export class EmailAutosave {
  constructor({ store, getPayload, onSaved, onCreated, initialBodyHtml = '', intervalMs = DEFAULT_INTERVAL_MS }) {
    this._store            = store
    this._getPayload       = getPayload        // () => { email_draft: {...} } | null
    this._onSaved          = onSaved           // () => void
    this._onCreated        = onCreated         // (id) => void
    this._intervalMs       = intervalMs
    this._timer            = null
    this._debounceTimer    = null
    this._lastBody         = null
    this._saving           = false
    // Snapshot of the body as it looked before the user typed anything
    // (signature-only for new compose, signature+quote for reply/forward).
    this._initialMeaningful = meaningfulText(initialBodyHtml)
  }

  start() {
    if (this._timer) return
    this._timer = setInterval(() => this.saveNow(), this._intervalMs)
  }

  stop() {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = null
  }

  // Call this on every input event for debounced save
  notifyChange() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null
      this.saveNow()
    }, DEBOUNCE_MS)
  }

  async saveNow() {
    if (this._saving) return
    const payload = this._getPayload?.()
    if (!payload) return

    const body = payload.email_draft?.body
    if (body === this._lastBody) return   // skip if unchanged
    if (!body && !this._store.draftId.value) return  // don't create empty draft

    // Don't create a brand-new draft if the body still only contains the
    // original signature/quote block (i.e. the user hasn't typed anything).
    // Once a draft exists, keep saving normally (e.g. addresses/subject changes).
    if (!this._store.draftId.value && meaningfulText(body) === this._initialMeaningful) {
      return
    }

    this._lastBody = body
    this._saving = true

    try {
      const draftId = this._store.draftId.value
      const draftFields = payload.email_draft || {}

      if (draftId) {
        await apiPatch(buildDraftPath(emailApiBase(), draftId), payload)
        this._store.upsertDraftInList?.({
          id: draftId,
          subject: draftFields.subject,
          body: draftFields.body,
          mailbox_id: draftFields.mailbox_id ? Number(draftFields.mailbox_id) : null,
          to_addresses: draftFields.to_addresses,
          draft_type: draftFields.draft_type,
          reply_to_email_id: draftFields.reply_to_email_id,
          snippet: meaningfulText(draftFields.body).slice(0, 120),
        })
        this._store.markComposeClean()
        this._onSaved?.()
      } else {
        const data = await apiPost(buildDraftPath(emailApiBase()), payload)
        const created = data?.draft || data
        if (created?.id) {
          this._store.setDraftId(created.id)
          this._onCreated?.(created.id)
          this._store.adjustSidebarCount('drafts', +1)
          this._store.upsertDraftInList?.({
            id: created.id,
            subject: draftFields.subject,
            body: draftFields.body,
            mailbox_id: draftFields.mailbox_id ? Number(draftFields.mailbox_id) : null,
            to_addresses: draftFields.to_addresses,
            draft_type: draftFields.draft_type,
            reply_to_email_id: draftFields.reply_to_email_id,
            snippet: meaningfulText(draftFields.body).slice(0, 120),
          })
        }
        this._store.markComposeClean()
        this._onSaved?.()
      }
    } catch (_) {
      // Silent — autosave failures should not interrupt the user
    } finally {
      this._saving = false
    }
  }

  async deleteDraft() {
    this.stop()
    const draftId = this._store.draftId.value
    if (!draftId) return
    try {
      await apiDelete(buildDraftPath(emailApiBase(), draftId))
      this._store.setDraftId(null)
      // Instant local drafts badge -1 (no ActionCable round-trip)
      this._store.adjustSidebarCount('drafts', -1)
      this._store.removeDraftFromList?.(draftId)
    } catch (_) {
      // Silent
    }
  }

  reset() {
    this._lastBody = null
  }
}
